import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useDownloadDocument, useOpenDocument } from '@/hooks/api/useDocuments';
import { usePollingJob } from '@/hooks/api/useInternalJobs';
import { useCollectors } from '@/hooks/api/useCollectors';
import { Paths } from '@/routes/paths';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Clock, Download, FileCheck, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader';

interface Props {
  jobId: string;
  collectorId: string;
}

export const CollectorInvoice = ({ jobId, collectorId }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: collectors } = useCollectors();
  const collector = collectors?.find(c => c.id === collectorId);

  const { data: jobStatus, error } = usePollingJob(jobId, { interval: 1000 });

  const isProgress = jobStatus?.status === 'in_progress' || jobStatus?.status === 'pending';
  const completed = jobStatus?.status === 'completed';
  const success = jobStatus?.result?.success === true;

  const invoices: { invoiceId: string; documentId: string; fileName: string; error?: string }[] = jobStatus?.result?.invoices || [];
  const progressData = jobStatus?.progress;
  const progressPercent = progressData?.total ? Math.round((progressData.processed / progressData.total) * 100) : 0;

  const combinedPdf = jobStatus?.result?.combinedPdf;
  const processingError = jobStatus?.error;
  const hasInternalErrors = invoices.some(inv => !!inv.error);

  const { mutate: downloadDocument } = useDownloadDocument()
  const { mutate: openDocument } = useOpenDocument()

  const handleDownloadDocument = (documentId: string, fileName?: string) => {
    if (!documentId) return
    downloadDocument({
      documentId,
      fileName: fileName || 'documento.pdf',
    })
  }

  const handleOpenDocument = (documentId: string, fileName?: string) => {
    if (!documentId) return
    openDocument({
      documentId,
      fileName: fileName || 'documento.pdf',
    })
  }

  const handleDownloadCombined = () => {
    if (!combinedPdf) return;
    const linkSource = `data:application/pdf;base64,${combinedPdf}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = `comprobantes_combinados_${collector?.name.replace(/\s+/g, '_') || 'cobrador'}_${new Date().toLocaleDateString('es-AR')}.pdf`;
    downloadLink.click();
  }

  const handleForward = () => {
    const prevSearch = (location.state as any)?.fromCollectorsSearch
    navigate({
      to: `${Paths.Management.Collectors}`,
      search: prevSearch || { page: 1, limit: 10, status: 'all', serviceType: 'all' },
    })
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error.message}</p>
        <Button variant="outline" onClick={() => {
          const prevSearch = (location.state as any)?.fromCollectorsSearch
          navigate({
            to: `${Paths.Management.Collectors}`,
            search: prevSearch || { page: 1, limit: 10, status: 'all', serviceType: 'all' },
          })
        }}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
      </div>
    );
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-2xl font-bold">Generación de comprobantes para el cobrador ({collector?.code}) {collector?.name}</h1>
      </NavHeader>

      <Card className="mt-6">
        {completed && success && !hasInternalErrors && (
          <CardHeader>
            <h1 className="text-2xl font-bold">Comprobantes generados</h1>
          </CardHeader>
        )}
        {combinedPdf && (
          <div className='flex justify-center gap-15 mb-10'>
            <Button variant={"info"} onClick={() => handleDownloadCombined()}>
              <Download className="h-4 w-4 mr-2" />
              Descargar PDF con todos los comprobantes
            </Button>
            <Button variant={"success"} onClick={handleForward}>
              Volver a cobradores
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}
        {completed && success && hasInternalErrors && (
          <CardHeader>
            <h1 className="text-2xl font-bold">Proceso completado con algunas advertencias</h1>
          </CardHeader>
        )}
        {(isProgress || !jobStatus) && (
          <CardHeader>
            <Alert variant={"default"}>
              <AlertTitle className="flex items-center mb-3">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando facturación
              </AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-4">
                  {progressData ? (
                    <>
                      <Progress value={progressPercent} className="w-full" />
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <span>Comprobantes generados: <strong>{progressData.processed}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5 text-amber-500">
                          <Clock className="h-4 w-4" />
                          <span>Comprobantes pendientes: <strong>{progressData.pending}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <span>Comprobantes totales: <strong>{progressData.total}</strong></span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p>Generando comprobantes, por favor espere...</p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          </CardHeader>
        )}
        <CardContent>
          <div className='flex flex-col items-start gap-10'>
            {((completed && !success) || processingError) && (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Error al generar facturas, contacte al administrador. Adjuntar código de referencia: {jobId}</p>
              </div>
            )}
            {completed && hasInternalErrors && (
              <Alert variant={"warning"}>
                <AlertTitle className="flex items-center mb-3">
                  <AlertCircle className="mr-2 h-4 w-4" />Atención
                </AlertTitle>
                <AlertDescription>
                  <p>El servicio de AFIP no se encuentra disponible para algunos pagos. Se generaron recibos temporales.</p>
                </AlertDescription>
              </Alert>
            )}

            {completed && success && invoices.length > 0 && (
              <div className='w-full flex flex-col gap-4'>
                {invoices.map((inv, index) => (
                  <div key={index} className='flex items-center justify-between p-4 border rounded'>
                    <div>
                      <p className='font-medium'>Comprobante N° {index + 1}</p>
                      <p className='text-sm text-gray-500'>{inv.fileName}</p>
                    </div>
                    <div className='flex gap-4'>
                      <Button variant={"secondary"} onClick={() => handleOpenDocument(inv.documentId, inv.fileName)}>
                        <FileCheck className="h-4 w-4 mr-2" />
                        Ver
                      </Button>
                      <Button variant={"secondary"} onClick={() => handleDownloadDocument(inv.documentId, inv.fileName)}>
                        <Download className="h-4 w-4 mr-2" />
                        Descargar
                      </Button>
                    </div>
                  </div>
                ))}

                {combinedPdf && (
                  <div className='flex justify-center gap-5 mt-6'>
                    <Button variant={"info"} onClick={() => handleDownloadCombined()}>
                      <Download className="h-4 w-4 mr-2" />
                      Descargar PDF con todos los comprobantes
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <div className='flex justify-center mt-10'>
        <Button variant={"success"} onClick={() => handleForward()}>
          Volver a cobradores
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </PageContainer>
  );
}
